import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/navigation/Header';

const FrequentlyAskedQuestions = () => {
  const navigate = useNavigate();
  const { user, userProfile, creditBalance, signOut } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User';
  const displayEmail = user?.email || '';

  const faqCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: 'Rocket',
      questions: [
        {
          id: 'what-is-angle',
          question: 'What is Angle! and how does it work?',
          answer: 'Angle! is an AI-powered photo ranking app that analyzes and compares your photos to help you choose the best ones. Simply upload up to 9 photos, and our advanced algorithms will rank them based on composition, lighting, facial expressions, and overall appeal. Perfect for selecting profile pictures, social media posts, or portfolio images.',
          popular: true
        },
        {
          id: 'how-to-start',
          question: 'How do I get started with Angle!?',
          answer: 'Getting started is easy! First, create an account or sign in. You\'ll receive 3 free credits to try the service. Then, navigate to the upload screen, select 2-10 photos you want to compare, and click "Analyze Photos". Within seconds, you\'ll receive detailed rankings and insights for each photo.',
          popular: true
        },
        {
          id: 'first-analysis',
          question: 'What should I expect from my first analysis?',
          answer: 'Your first analysis will provide a ranked list of your photos from best to worst, along with detailed scores for composition, lighting, facial expression (if applicable), and overall appeal. You\'ll also receive actionable feedback on what makes each photo strong or weak, helping you understand what works best for your needs.'
        },
        {
          id: 'supported-formats',
          question: 'What photo formats are supported?',
          answer: 'Angle! supports JPG, JPEG, PNG, and HEIC formats. Each photo must be under 10MB in size. For best results, use high-resolution images with good lighting. The app works with portraits, selfies, group photos, and even product shots.'
        }
      ]
    },
    {
      id: 'photo-analysis',
      title: 'Photo Analysis',
      icon: 'Image',
      questions: [
        {
          id: 'how-ranking-works',
          question: 'How does the photo ranking algorithm work?',
          answer: 'Our AI analyzes multiple factors including composition (rule of thirds, symmetry, balance), lighting quality (exposure, contrast, color temperature), facial expressions (if present), background clarity, and overall aesthetic appeal. The algorithm has been trained on millions of professional photos to understand what makes images visually compelling.',
          popular: true
        },
        {
          id: 'photos-per-credit',
          question: 'How many photos can I analyze per credit?',
          answer: 'Each credit allows you to analyze one batch of 2-10 photos with full ranking and comparison features. You can upload anywhere from 2 to 10 photos in a single analysis session. The more photos you include, the more comprehensive your comparison results will be.',
          popular: true
        },
        {
          id: 'analysis-time',
          question: 'How long does photo analysis take?',
          answer: 'Most analyses complete within 10-30 seconds, depending on the number of photos and their file sizes. You\'ll see a progress indicator during processing, and you can navigate away from the page - we\'ll save your results in your analysis history.'
        },
        {
          id: 'reanalyze-photos',
          question: 'Can I re-analyze the same photos?',
          answer: 'Yes! You can re-analyze photos at any time, and each analysis will use one credit. This is useful if you\'ve edited photos and want to compare the improvements, or if you want to compare different combinations of images.'
        },
        {
          id: 'photo-types',
          question: 'What types of photos work best?',
          answer: 'Angle! works great with portraits, selfies, headshots, group photos, and even product photography. For best results, ensure photos are well-lit, in focus, and show clear subjects. The app is optimized for photos where you want to evaluate visual appeal and composition quality.'
        }
      ]
    },
    {
      id: 'credits-billing',
      title: 'Credits & Billing',
      icon: 'CreditCard',
      questions: [
        {
          id: 'credit-expiration',
          question: 'Do credits expire?',
          answer: 'Credits are valid for 6–12 months depending on your package. Starter Pack credits expire after 6 months, while Popular Pack and Pro Pack credits are valid for 12 months. You\'ll receive email reminders before expiration, giving you plenty of time to use them.',
          popular: true
        },
        {
          id: 'purchase-credits',
          question: 'How do I purchase credits?',
          answer: 'Click the "Purchase Credits" button in the header or when you run out of credits. Choose from our Starter Pack (10 credits for $9.99), Popular Pack (25 credits for $19.99), or Pro Pack (50 credits for $34.99). We accept all major credit cards, debit cards, and digital wallets through secure Stripe payment processing.'
        },
        {
          id: 'refund-policy',
          question: 'What is your refund policy?',
          answer: 'Unused credits can be refunded within 7 days of purchase. If you\'ve used some credits from a package, we\'ll refund the remaining unused credits on a pro-rated basis. Contact our support team with your order details to initiate a refund request.',
          popular: true
        },
        {
          id: 'payment-methods',
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover), debit cards, and digital wallets including Apple Pay and Google Pay. All payments are processed securely through Stripe, and we never store your payment information on our servers.'
        },
        {
          id: 'subscription-plans',
          question: 'Do you offer subscription plans?',
          answer: 'Currently, we operate on a credit-based system rather than subscriptions. This gives you flexibility to purchase credits only when you need them, without recurring charges. However, we\'re exploring subscription options for power users - stay tuned for updates!'
        },
        {
          id: 'free-credits',
          question: 'Can I get free credits?',
          answer: 'New users receive 3 free credits upon signup to try the service. We also occasionally offer promotional credits through our newsletter and social media channels. Keep an eye on our announcements for special offers.'
        }
      ]
    },
    {
      id: 'technical-support',
      title: 'Technical Support',
      icon: 'Settings',
      questions: [
        {
          id: 'upload-issues',
          question: 'Why won\'t my photos upload?',
          answer: 'Common upload issues include: file size exceeding 10MB (try compressing images), unsupported format (use JPG, PNG, or HEIC), poor internet connection, or browser cache issues. Try clearing your browser cache, using a different browser, or reducing image file sizes. If problems persist, contact support with details about your device and browser.'
        },
        {
          id: 'browser-compatibility',
          question: 'Which browsers are supported?',
          answer: 'Angle! works best on the latest versions of Chrome, Firefox, Safari, and Edge. We recommend keeping your browser updated for optimal performance and security. Mobile browsers on iOS Safari and Android Chrome are fully supported.'
        },
        {
          id: 'mobile-app',
          question: 'Is there a mobile app?',
          answer: 'Currently, Angle! is a web-based application optimized for mobile browsers. You can access it from any smartphone or tablet browser and add it to your home screen for app-like experience. Native iOS and Android apps are in development and coming soon!'
        },
        {
          id: 'slow-analysis',
          question: 'Why is my analysis taking so long?',
          answer: 'Analysis time can be affected by large file sizes, slow internet connection, or high server load during peak hours. Most analyses complete in 10-30 seconds. If your analysis is taking longer than 2 minutes, try refreshing the page or re-uploading with smaller file sizes.'
        },
        {
          id: 'results-not-showing',
          question: 'My results aren\'t showing up. What should I do?',
          answer: 'First, check your Analysis History page - results are automatically saved there. If you still don\'t see them, try refreshing the page or clearing your browser cache. If the issue persists, contact support with your analysis timestamp and we\'ll retrieve your results manually.'
        }
      ]
    },
    {
      id: 'privacy-security',
      title: 'Privacy & Security',
      icon: 'Shield',
      questions: [
        {
          id: 'photo-privacy',
          question: 'What happens to my photos after analysis?',
          answer: 'Your privacy is our top priority. Photos are securely stored for 30 days to allow you to access your analysis history, then automatically deleted from our servers. You can manually delete photos from your history at any time. We never share, sell, or use your photos for any purpose other than providing the analysis service.',
          popular: true
        },
        {
          id: 'data-security',
          question: 'How secure is my data?',
          answer: 'We use industry-standard encryption (SSL/TLS) for all data transmission and storage. Photos are stored in secure, encrypted cloud storage with restricted access. Our servers are regularly audited for security vulnerabilities, and we comply with GDPR and CCPA privacy regulations.'
        },
        {
          id: 'account-security',
          question: 'How can I secure my account?',
          answer: 'Use a strong, unique password for your Angle! account. We recommend enabling two-factor authentication (coming soon) for additional security. Never share your login credentials, and log out from shared devices. If you suspect unauthorized access, change your password immediately and contact support.'
        },
        {
          id: 'delete-account',
          question: 'How do I delete my account and data?',
          answer: 'You can delete your account anytime from Account Settings. This will permanently remove all your photos, analysis history, and personal information from our servers within 48 hours. Unused credits will be forfeited, but you can request a refund for recent purchases before deletion.'
        },
        {
          id: 'third-party-sharing',
          question: 'Do you share data with third parties?',
          answer: 'We do not sell or share your photos or personal information with third parties for marketing purposes. We only share data with essential service providers (payment processing, cloud storage, analytics) who are contractually bound to protect your privacy. You can review our full privacy policy for details.'
        }
      ]
    }
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqCategories?.flatMap(cat =>
      cat?.questions?.map(q => ({
        "@type": "Question",
        "name": q?.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": q?.answer
        }
      }))
    )
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev?.[categoryId]
    }));
  };

  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev?.[questionId]
    }));
  };

  const handleFeedback = (questionId, isHelpful) => {
    setFeedbackGiven(prev => ({
      ...prev,
      [questionId]: isHelpful ? 'positive' : 'negative'
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
  };

  const filteredCategories = useMemo(() => {
    if (!searchQuery?.trim()) return faqCategories;

    const query = searchQuery?.toLowerCase();
    return faqCategories?.map(category => ({
      ...category,
      questions: category?.questions?.filter(q => 
        q?.question?.toLowerCase()?.includes(query) || 
        q?.answer?.toLowerCase()?.includes(query)
      )
    }))?.filter(category => category?.questions?.length > 0);
  }, [searchQuery]);

  const handleCameraClick = () => {
    document.getElementById('camera-file-input')?.click();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Frequently Asked Questions | Angle! Photo Ranking App</title>
        <meta name="description" content="Find answers to common questions about Angle! – how photo ranking works, credits, pricing, privacy, and more." />
        <link rel="canonical" href="https://angle.dating/frequently-asked-questions" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://angle.dating/frequently-asked-questions" />
        <meta property="og:title" content="Frequently Asked Questions | Angle!" />
        <meta property="og:description" content="Find answers to common questions about Angle! – how photo ranking works, credits, pricing, privacy, and more." />
        <meta property="og:site_name" content="Angle!" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Frequently Asked Questions | Angle!" />
        <meta name="twitter:description" content="Find answers to common questions about Angle! – how photo ranking works, credits, pricing, privacy, and more." />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <Header
        user={user}
        onNavigate={navigate}
        onPurchaseCredits={() => navigate('/purchase-credits')}
        onLogout={handleLogout}
      />
      <main className="main-content">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header with decorative elements */}
          <div className="text-center mb-8 relative">
            {/* Decorative floating icons */}
            <div className="absolute -top-4 left-8 animate-float">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-cyan-100 flex items-center justify-center">
                <Icon name="MessageCircle" size={20} color="#9333EA" />
              </div>
            </div>
            <div className="absolute -top-4 right-8 animate-float-delayed">
              <Icon name="Sparkles" size={24} color="#06B6D4" />
            </div>
            
            <div className="flex items-center justify-center gap-3 mb-4">
              {/* Main Icon with Gradient Background */}
              <div className="relative inline-block">
                {/* Outer glow circle */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-200 to-cyan-200 rounded-full blur-xl opacity-40 scale-110"></div>
                {/* Middle circle */}
                <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-purple-100 to-cyan-100 flex items-center justify-center">
                  {/* Inner circle with icon */}
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center">
                    <Icon name="HelpCircle" size={24} color="#FFFFFF" />
                  </div>
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-transparent">
                Frequently Asked Questions
              </h1>
            </div>
            <p className="text-lg text-gray-600 mb-6">
              Find answers to common questions about Angle! and get the most out of your photo analysis experience.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <div className="relative">
                <Icon 
                  name="Search" 
                  size={20} 
                  color="#9CA3AF" 
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none"
                />
                <input
                  type="text"
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent text-gray-900 placeholder-gray-400"
                />
              </div>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" size={20} />
                </button>
              )}
            </div>
          </div>

          {/* FAQ Categories */}
          <div className="space-y-6">
            {filteredCategories?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="SearchX" size={48} color="#9CA3AF" className="mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-600">Try different keywords or browse all categories below</p>
                <Button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Clear Search
                </Button>
              </div>
            ) : (
              filteredCategories?.map(category => (
                <div key={category?.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category?.id)}
                    className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-cyan-100 rounded-lg flex items-center justify-center">
                        <Icon name={category?.icon} size={24} color="#9333EA" />
                      </div>
                      <div className="text-left">
                        <h2 className="text-xl font-bold text-gray-900">{category?.title}</h2>
                        <p className="text-sm text-gray-500">{category?.questions?.length} questions</p>
                      </div>
                    </div>
                    <Icon 
                      name={expandedCategories?.[category?.id] ? "ChevronUp" : "ChevronDown"} 
                      size={24} 
                      color="#6B7280" 
                    />
                  </button>

                  {/* Category Questions */}
                  {expandedCategories?.[category?.id] && (
                    <div className="border-t border-gray-200">
                      {category?.questions?.map((q, index) => (
                        <div 
                          key={q?.id} 
                          className={`${index !== 0 ? 'border-t border-gray-100' : ''}`}
                        >
                          {/* Question Header */}
                          <button
                            onClick={() => toggleQuestion(q?.id)}
                            className="w-full flex items-start justify-between p-6 hover:bg-gray-50 transition-colors text-left"
                          >
                            <div className="flex items-start gap-3 flex-1">
                              {q?.popular && (
                                <div className="flex-shrink-0 mt-1">
                                  <Icon name="Star" size={18} color="#F59E0B" className="fill-yellow-400" />
                                </div>
                              )}
                              <h3 className="font-semibold text-gray-900 flex-1">{q?.question}</h3>
                            </div>
                            <Icon 
                              name={expandedQuestions?.[q?.id] ? "ChevronUp" : "ChevronDown"} 
                              size={20} 
                              color="#6B7280"
                              className="flex-shrink-0 ml-4 mt-1"
                            />
                          </button>

                          {/* Answer */}
                          {expandedQuestions?.[q?.id] && (
                            <div className="px-6 pb-6">
                              <div className="bg-gradient-to-r from-purple-50 to-cyan-50 rounded-lg p-4 mb-4">
                                <p className="text-gray-700 leading-relaxed">{q?.answer}</p>
                              </div>

                              {/* Feedback & Actions */}
                              <div className="flex items-center justify-between flex-wrap gap-4">
                                <div className="flex items-center gap-4">
                                  <span className="text-sm text-gray-600">Was this helpful?</span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleFeedback(q?.id, true)}
                                      className={`p-2 rounded-lg transition-colors ${
                                        feedbackGiven?.[q?.id] === 'positive' ?'bg-emerald-100 text-emerald-600' :'hover:bg-gray-100 text-gray-400'
                                      }`}
                                    >
                                      <Icon name="ThumbsUp" size={18} />
                                    </button>
                                    <button
                                      onClick={() => handleFeedback(q?.id, false)}
                                      className={`p-2 rounded-lg transition-colors ${
                                        feedbackGiven?.[q?.id] === 'negative' ?'bg-red-100 text-red-600' :'hover:bg-gray-100 text-gray-400'
                                      }`}
                                    >
                                      <Icon name="ThumbsDown" size={18} />
                                    </button>
                                  </div>
                                </div>
                                <button
                                  onClick={() => copyToClipboard(q?.answer)}
                                  className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
                                >
                                  <Icon name="Copy" size={16} />
                                  Copy Answer
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Still Need Help Section */}
          <div className="mt-12 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-xl p-8 text-center text-white">
            <Icon name="MessageCircle" size={48} className="mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Still need help?</h3>
            <p className="text-purple-100 mb-6 max-w-2xl mx-auto">
              Can't find the answer you're looking for? Our support team is here to help you with any questions or concerns.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => window.location.href = 'mailto:support@notifications.angle.dating'}
                className="bg-white text-purple-600 hover:bg-gray-100 font-semibold px-6 py-3"
              >
                <Icon name="Mail" size={20} className="mr-2" />
                Email Support
              </Button>
            </div>
          </div>
        </div>
      </main>
      {/* Hidden file input for camera button */}
      <input
        id="camera-file-input"
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,image/heic,image/jpg"
        multiple
      />
      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-around z-40">
        <button 
          onClick={handleCameraClick}
          className="flex flex-col items-center gap-1.5 hover:opacity-80 transition-opacity"
        >
          <Icon name="Camera" size={28} color="#9CA3AF" />
          <span className="text-sm text-gray-400">Camera</span>
        </button>
        <button 
          onClick={() => navigate('/photo-upload-interface')}
          className="flex flex-col items-center gap-1.5 hover:opacity-80 transition-opacity"
        >
          <Icon name="Image" size={28} color="#9CA3AF" />
          <span className="text-sm text-gray-400">Rank</span>
        </button>
        <button 
          onClick={() => navigate('/ranking-results-display')}
          className="flex flex-col items-center gap-1.5 hover:opacity-80 transition-opacity"
        >
          <Icon name="BarChart2" size={28} color="#9CA3AF" />
          <span className="text-sm text-gray-400">Results</span>
        </button>
        <button 
          onClick={toggleMobileMenu}
          className="flex flex-col items-center gap-1.5 hover:opacity-80 transition-opacity"
        >
          <Icon name="Menu" size={28} color="#9CA3AF" />
          <span className="text-sm text-gray-400">Menu</span>
        </button>
      </div>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-black/50 z-50" onClick={toggleMobileMenu}>
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Menu</h3>
              <button onClick={toggleMobileMenu} className="p-2">
                <Icon name="X" size={24} color="#6B7280" />
              </button>
            </div>

            {/* User Info */}
            <div className="bg-gradient-to-r from-purple-50 to-cyan-50 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">{displayName?.charAt(0)}</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{displayName}</div>
                  <div className="text-sm text-gray-600">{displayEmail}</div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-purple-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Credits Remaining</span>
                  <span className="text-lg font-bold text-purple-600">{creditBalance}</span>
                </div>
              </div>
            </div>

            {/* Navigation Links */}
            <div className="space-y-2 mb-6">
              <button
                onClick={() => {
                  navigate('/user-dashboard');
                  toggleMobileMenu();
                }}
                className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Icon name="Home" size={24} color="#9333EA" />
                <div className="text-left">
                  <div className="font-medium text-gray-900">Home</div>
                  <div className="text-xs text-gray-500">Go to your dashboard</div>
                </div>
              </button>
              <button
                onClick={() => {
                  navigate('/photo-upload-interface');
                  toggleMobileMenu();
                }}
                className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Icon name="Upload" size={24} color="#9333EA" />
                <div className="text-left">
                  <div className="font-medium text-gray-900">Upload Photos</div>
                  <div className="text-xs text-gray-500">Upload and analyze your photos</div>
                </div>
              </button>
              <button
                onClick={() => {
                  navigate('/ranking-results-display');
                  toggleMobileMenu();
                }}
                className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Icon name="BarChart3" size={24} color="#9333EA" />
                <div className="text-left">
                  <div className="font-medium text-gray-900">View Results</div>
                  <div className="text-xs text-gray-500">See your photo rankings</div>
                </div>
              </button>
              <button
                onClick={() => {
                  navigate('/dashboard-analysis-history');
                  toggleMobileMenu();
                }}
                className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Icon name="History" size={24} color="#9333EA" />
                <div className="text-left">
                  <div className="font-medium text-gray-900">History</div>
                  <div className="text-xs text-gray-500">View past analyses</div>
                </div>
              </button>
              <button
                onClick={() => {
                  navigate('/frequently-asked-questions');
                  toggleMobileMenu();
                }}
                className="w-full flex items-center gap-3 p-4 rounded-xl bg-purple-50 border-2 border-purple-600 transition-colors"
              >
                <Icon name="HelpCircle" size={24} color="#9333EA" />
                <div className="text-left">
                  <div className="font-medium text-purple-600">FAQ</div>
                  <div className="text-xs text-purple-500">Frequently asked questions</div>
                </div>
              </button>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  navigate('/purchase-credits');
                  toggleMobileMenu();
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity"
              >
                Purchase Credits
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  toggleMobileMenu();
                }}
                className="w-full flex items-center justify-center gap-2 text-gray-600 font-medium py-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Icon name="LogOut" size={20} color="#6B7280" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrequentlyAskedQuestions;